import { useEffect, useState } from "react";
import { Entry } from "@models/entry";
import { ENTRY_BROWSE_PER_PAGE, buildEntrySort } from "@features/tickets/api/entriesBrowse.utils";
import { useSearchEntriesBrowseQuery } from "@features/tickets/api/entries.api";
import { useErrorBoundary } from "react-error-boundary";
import { handleErrorWithBoundary } from "@shared/lib/errors";
import { DEFAULT_SORT_KEY, DEFAULT_SORT_METHOD } from "../utils/browseUrlParams";
import { useBrowsePagination } from "./useBrowsePagination";
import { isBrowseCacheEligible } from "./useBrowseCacheEligible";

export function useEntriesBrowse(accountId: number | undefined) {
  const { showBoundary } = useErrorBoundary();
  const [data, setData] = useState<Entry[]>([]);
  const [sortKey, setSortKey] = useState(DEFAULT_SORT_KEY);
  const [sortMethod, setSortMethod] = useState<"ASC" | "DESC">(DEFAULT_SORT_METHOD);
  const pagination = useBrowsePagination(ENTRY_BROWSE_PER_PAGE);

  const filter = accountId ? { account_id: accountId } : {};

  const {
    data: queryData,
    isFetching: isLoading,
    error,
  } = useSearchEntriesBrowseQuery(
    {
      accountId: accountId ?? 0,
      filter,
      sort: [buildEntrySort(sortKey, sortMethod)],
      per_page: pagination.perPage,
      cursor: pagination.cursor,
      useCache: isBrowseCacheEligible({
        filters: filter,
        sortKey,
        sortMethod,
        cursor: pagination.cursor,
        allowedFilterKeys: ["account_id"],
      }),
    },
    { skip: !accountId },
  );

  useEffect(() => {
    if (!accountId) {
      setData([]);
    }
  }, [accountId]);

  useEffect(() => {
    if (queryData) {
      setData(queryData.data);
    }
  }, [queryData]);

  useEffect(() => {
    if (error) {
      handleErrorWithBoundary(error, showBoundary, undefined, "backgroundQuery");
    }
  }, [error, showBoundary]);

  function handleSortKeyChanged(value: string) {
    pagination.resetPagination();
    setSortKey(value);
  }

  function handleSortMethodChanged(value: string) {
    pagination.resetPagination();
    setSortMethod(value === "ASC" ? "ASC" : "DESC");
  }

  function handleNextPage() {
    pagination.handleNextPage(queryData?.meta.next_cursor);
  }

  function handlePrevPage() {
    pagination.handlePrevPage(queryData?.meta.prev_cursor);
  }

  return {
    data,
    isLoading: Boolean(accountId) && isLoading,
    sortKey,
    sortMethod,
    handleSortKeyChanged,
    handleSortMethodChanged,
    page: pagination.page,
    perPage: pagination.perPage,
    hasNextPage: Boolean(queryData?.meta.next_cursor),
    hasPrevPage: Boolean(queryData?.meta.prev_cursor),
    handleNextPage,
    handlePrevPage,
    handleRowsPerPageChange: pagination.handleRowsPerPageChange,
  };
}
